// app_client/src/pages/admin/InvoiceDetail.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Ban, BriefcaseBusiness, Loader2, Save, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';

import useExchangeStore from '../../store/useExchangeStore';
import useInvoiceStore from '../../store/useInvoiceStore';
import useInvoiceCalculator from '../../hooks/useInvoiceCalculator';

import InvoiceItemsTable from '../../components/invoices/InvoiceItemsTable';
import InvoiceFooter from '../../components/invoices/InvoiceFooter';
import InvoiceHeaderCard from '../../components/invoices/InvoiceHeaderCard';

import AccountSelectModal from '../../components/modals/AccountSelectModal';
import ProductSelectModal from '../../components/modals/ProductSelectModal';
import OperationSearchDialog from '../../components/modals/OperationSearchDialog';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';

const InvoiceDetail = () => {
  const { id } = useParams();
  const isCreateMode = !id || id === 'new';
  const isEditMode = !isCreateMode;
  const navigate = useNavigate();
  const { confirm } = useConfirmation();

  const { fetchRates } = useExchangeStore();
  
  const { 
    activeInvoice, 
    updateActiveInvoiceField, 
    fetchInvoiceById, 
    saveInvoice, 
    updateInvoiceStatus,
    fetchInvoiceDependencies,
    deleteInvoice,
    resetForm,
    clearCurrentInvoice,
    loading, 
    setActiveInvoiceItems 
  } = useInvoiceStore();

  const { formatNewProduct, calculateAllTotals } = useInvoiceCalculator();

  const [isAccModalOpen, setIsAccModalOpen] = useState(false);
  const [isProdModalOpen, setIsProdModalOpen] = useState(false);
  const [isOperationDialogOpen, setIsOperationDialogOpen] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const initialInvoiceRef = useRef(null);

  // Store'u toplu güncellemek için yardımcı fonksiyon
  const updateActiveInvoice = useCallback((data) => {
    Object.keys(data || {}).forEach(key => {
      updateActiveInvoiceField(key, data?.[key]);
    });
  }, [updateActiveInvoiceField]);

  useEffect(() => {
    fetchRates();

    if (isEditMode) {
      fetchInvoiceById(id).then(data => {
        if (data) {
          initialInvoiceRef.current = data;
          updateActiveInvoice(data);
        }
      });
      return;
    }

    const initialInvoice = {
      date: new Date().toISOString().split('T')[0],
      items: [],
      currency: 'TRY',
      exchangeRate: 1,
      type: 'Sales',
      subTotal: 0,
      totalVat: 0,
      grandTotal: 0,
      paidAmount: 0,
      remainingAmount: 0,
      paymentStatus: 'Unpaid',
      accountName: '',
      accountId: '',
      operation: null,
      invoiceNumber: '',
      notes: ''
    };

    initialInvoiceRef.current = initialInvoice;
    resetForm();
    updateActiveInvoice(initialInvoice);
  }, [id, isCreateMode, isEditMode, fetchRates, fetchInvoiceById, resetForm, updateActiveInvoice]);

  if (!activeInvoice) return null;

  const isLocked = Number(activeInvoice?.paidAmount || 0) > 0 || ['Paid', 'PartiallyPaid'].includes(activeInvoice?.paymentStatus);
  const isCancelled = activeInvoice?.status === 'Cancelled';
  const assignedOperation = activeInvoice?.operation || null;
  const isAssignedOperationObject = Boolean(assignedOperation && typeof assignedOperation === 'object');
  const assignedOperationId = assignedOperation?._id || assignedOperation || null;
  const hasAssignedOperation = Boolean(assignedOperationId);
  const assignedOperationLabel = isAssignedOperationObject
    ? (assignedOperation?.projectNo || assignedOperation?.name || assignedOperationId || 'Operasyon Atanmamış')
    : (assignedOperationId || 'Operasyon Atanmamış');
  const assignedOperationAccount = isAssignedOperationObject
    ? assignedOperation?.account?.name
    : null;

  const handleProductSelect = (prod) => {
    if (isLocked) return;
    const calculatedItem = formatNewProduct(prod);
    if (!calculatedItem || !calculatedItem.productId) return;

    // Güncel listeyi kopyala[cite: 4]
    const currentItems = [...(activeInvoice?.items || [])];
    let newItems = [...currentItems];

    if (activeItemIndex !== null && activeItemIndex !== undefined) {
      // Mevcut satırı güncelle
      newItems[activeItemIndex] = calculatedItem;
    } else {
      // Yeni satır ekle
      newItems.push(calculatedItem);
    }

    // Hem tabloyu hem toplamları yeni listeyle güncelle[cite: 4]
    setActiveInvoiceItems(newItems);
    calculateAllTotals(newItems);
  
    setIsProdModalOpen(false);
    setActiveItemIndex(null); 

    console.log("Hesaplanan Toplamlar:", newItems);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (isCancelled) return toast.error("İptal edilmiş belge güncellenemez.");
    if (!activeInvoice?.accountId) return toast.error("Cari hesap seçin.");
    if (!activeInvoice?.items || activeInvoice?.items?.length === 0) return toast.error("En az bir kalem eklemelisiniz.");
    
    if (isEditMode && !id) {
      return toast.error("Fatura ID geçersiz.");
    }

    const invoicePayload = isLocked ? {
      notes: activeInvoice.notes || '',
      description: activeInvoice.description || '',
      dueDate: activeInvoice.dueDate || null
    } : {
      ...activeInvoice,
      operation: activeInvoice?.operation?._id || activeInvoice?.operation || null
    };

    const res = await saveInvoice(invoicePayload, isCreateMode ? null : id);
    if (res?.success) {
      navigate('/invoices', { state: { refreshList: true } });
    } else {
      toast.error(res?.message || 'Fatura kaydedilemedi.');
    }
  };

  const handleCancel = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const hasChanges = JSON.stringify(activeInvoice || null) !== JSON.stringify(initialInvoiceRef.current || null);
    if (hasChanges && !window.confirm('Kaydedilmemiş değişiklikler var. Çıkmak istediğinize emin misiniz?')) {
      return;
    }

    clearCurrentInvoice();
    navigate('/invoices');
  };

  const handleCancelInvoice = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (isCreateMode || isCancelled || !id) return;
    if (isLocked) return toast.error('Önce ilgili finansal eşleştirmeleri kaldırınız.');

    const dependenciesResult = await fetchInvoiceDependencies(id);
    if (!dependenciesResult.success) {
      toast.error(dependenciesResult.message);
      return;
    }

    const transactionCount = dependenciesResult.data?.counts?.transactions || 0;

    if (transactionCount > 0) {
      const decision = await confirm({
        title: 'Fatura Bağlı Hareket İçeriyor',
        message: `Bu faturaya bağlı ${transactionCount} adet hareket bulunmaktadır. Tüm hareketleri silip faturayı tamamen kaldırmak mı istersiniz, yoksa faturayı iptal mi etmek istersiniz?`,
        confirmText: 'Tümünü Sil ve Kaldır',
        cancelText: 'Sadece İptal Et',
        confirmValue: 'delete',
        cancelValue: 'cancel',
        dismissValue: null
      });

      if (decision === 'delete') {
        const reasonResult = await confirm({
          title: 'Silme Nedeni',
          message: 'Faturayı tamamen kaldırmak için silme nedenini yazın.',
          confirmText: 'Sil',
          cancelText: 'Vazgeç',
          requireReason: true,
          reasonLabel: 'Silme nedeni',
          reasonPlaceholder: 'Örn: Hatalı kayıt, fatura iptali...'
        });

        if (!reasonResult) return;

        const res = await deleteInvoice(id, reasonResult.reason);
        if (res?.success) {
          toast.success('Fatura ve bağlı hareketler silindi.');
          navigate('/invoices', { state: { refreshList: true } });
        } else {
          toast.error(res?.message || 'Fatura silinemedi.', { duration: 6000 });
        }
        return;
      }

      if (decision !== 'cancel') return;
    } else if (!await confirm("Bu faturayı iptal etmek istediğinize emin misiniz? Cari ve stok etkileri geri alınacak.")) {
      return;
    }

    const res = await updateInvoiceStatus(id, 'Cancelled');
    if (res?.success) {
      updateActiveInvoice(res.data);
      toast.success("Fatura iptal edildi.");
      navigate('/invoices', { state: { refreshList: true } });
    } else {
      toast.error(res?.message || "Fatura iptal edilemedi.");
    }
  };

  const handleOperationSelect = async (operation) => {
    if (isLocked) return;
    if (!operation?._id) {
      toast.error('Geçerli bir operasyon seçin.');
      return;
    }

    updateActiveInvoiceField('operation', operation);
    setIsOperationDialogOpen(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Bar */}
      <div className="flex justify-between items-center bg-white p-4 rounded-4xl border border-slate-100 shadow-sm">
        <button type="button" onClick={handleCancel} className="group flex items-center gap-2 font-bold text-slate-700 hover:text-slate-900 transition-colors">
          <ArrowLeft size={20} /> Fatura Listesi
        </button>
        <div className="flex gap-2">
          {isEditMode && !isCancelled && (
            <button
              type="button"
              onClick={handleCancelInvoice}
              disabled={loading || isLocked}
              className="flex items-center gap-2 px-6 py-3 rounded-2xl font-black bg-rose-50 text-rose-600 hover:bg-rose-100 disabled:opacity-50 transition-all"
            >
              <Ban size={20} />
              <span>İptal Et</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit} 
            disabled={loading || isCancelled} 
            className="flex items-center gap-2 px-8 py-3 rounded-2xl font-black bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 transition-all"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Save size={20}/>}
            <span>Kaydet</span>
          </button>
        </div>
      </div>

      {isLocked && <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold text-amber-800">Bu fatura eşleştirilmiş bir işleme sahiptir. Kritik alanları değiştirmek için önceden yapılmış eşleştirmeleri kaldırınız.</div>}

      {isCancelled && (
        <div className="bg-rose-50 border border-rose-100 text-rose-700 rounded-[2rem] px-8 py-5 flex items-center justify-center">
          <div className="text-center">
            <p className="text-2xl font-black tracking-[0.25em] uppercase">İptal Edilmiş Belge</p>
            <p className="text-xs font-bold mt-1">Bu faturanın cari ve stok etkileri geri alınmıştır.</p>
          </div>
        </div>
      )}

      <div className={isCancelled ? 'pointer-events-none opacity-60' : ''}>
        {/* Üst Bilgi Kartı */}
        <InvoiceHeaderCard locked={isLocked} />

        <div className={`border p-5 rounded-[2rem] shadow-sm ${
          hasAssignedOperation
            ? 'bg-indigo-50 border-indigo-100'
            : 'bg-amber-50 border-amber-100'
        }`}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${
                hasAssignedOperation ? 'bg-indigo-100 text-indigo-600' : 'bg-amber-100 text-amber-600'
              }`}>
                {hasAssignedOperation ? <BriefcaseBusiness size={20} /> : <AlertTriangle size={20} />}
              </div>
              <div>
                <p className={`text-[10px] font-black uppercase ${
                  hasAssignedOperation ? 'text-indigo-500' : 'text-amber-600'
                }`}>
                  {hasAssignedOperation ? 'Seçili Operasyon' : 'Operasyon Seçilmedi'}
                </p>
                <p className="font-black text-slate-800">
                  {hasAssignedOperation ? (assignedOperationLabel || 'Operasyon Atanmamış') : 'Faturayı bir operasyona bağlayabilirsiniz.'}
                </p>
                {assignedOperationAccount && (
                  <p className="text-xs font-bold text-slate-500 mt-0.5">{assignedOperationAccount}</p>
                )}
              </div>
            </div>
            {!isCancelled && (
              <button
                type="button"
                onClick={() => setIsOperationDialogOpen(true)}
                disabled={loading || isLocked}
                className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-black text-sm disabled:opacity-50 transition-all ${
                  hasAssignedOperation
                    ? 'bg-white text-indigo-700 hover:bg-indigo-100 border border-indigo-100'
                    : 'bg-amber-600 text-white hover:bg-amber-700'
                }`}
              >
                <BriefcaseBusiness size={18} />
                <span>{hasAssignedOperation ? 'Değiştir' : 'Operasyon Seçiniz'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Fatura İçeriği */}
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <button 
              type="button"
              disabled={isLocked} onClick={() => setIsAccModalOpen(true)}
              className="flex items-center justify-between px-6 py-4 bg-slate-50 hover:bg-slate-100 rounded-2xl text-left transition-colors"
            >
              <div className="flex flex-col">
                 <span className="text-[10px] font-black text-slate-900 uppercase">Cari Hesap</span>
                 <span className="font-bold text-slate-900">{activeInvoice?.accountName || activeInvoice?.accountId?.name || "Seçiniz..."}</span>
              </div>
              <Search size={18} className="text-slate-400" />
            </button>
            <div className="flex flex-col px-6 py-3 bg-slate-50 rounded-2xl">
              <label className="text-[10px] font-black text-slate-900 uppercase">Belge No</label>
              <input 
                disabled={isLocked} placeholder="Fatura/İrsaliye No..."
                value={activeInvoice?.invoiceNumber || ''} 
                onChange={e => updateActiveInvoiceField('invoiceNumber', e.target.value)} 
                className="bg-transparent font-bold outline-none text-slate-900 mt-0.5" 
              />
            </div>
          </div>

          {/* Kalemler Tablosu */}
          <fieldset disabled={isLocked} className="min-w-0 disabled:opacity-60">
          <InvoiceItemsTable 
            onOpenProductModal={(index = null) => { 
              if (isLocked) return;
              setActiveItemIndex(index); 
              setIsProdModalOpen(true); 
            }} 
          />

          </fieldset>
          {/* Alt Toplamlar ve Notlar */}
          <InvoiceFooter />
        </div>
      </div>

      {/* Modallar */}
      <AccountSelectModal 
        isOpen={isAccModalOpen} 
        onClose={() => setIsAccModalOpen(false)} 
        onSelect={(acc) => {
          updateActiveInvoiceField('accountId', acc?._id || '');
          updateActiveInvoiceField('accountName', acc?.name || '');
        }} 
      />
      
      <ProductSelectModal 
        key={isProdModalOpen ? 'open' : 'closed'} 
        isOpen={isProdModalOpen} 
        onClose={() => {
          setIsProdModalOpen(false);
          setActiveItemIndex(null);
        }} 
        onSelect={handleProductSelect} 
      />

      <OperationSearchDialog
        isOpen={isOperationDialogOpen}
        onClose={() => setIsOperationDialogOpen(false)}
        onSelect={handleOperationSelect}
        disabled={loading || isLocked}
      />
    </div>
  );
};

export default InvoiceDetail;
